import React, { useState } from 'react';
import Customer from '../../../types/Customer';
import InputField from '../../input-field';
import useEditCustomer from '../../../hooks/use-edit-customer';
import CustomerInput from '../../../types/CustomerInput';
import Button from '../../button';
import useStates from '../../../hooks/use-states';
import useCountries from '../../../hooks/use-countries';
import DropdownField from '../../dropdown-field';
import phoneFormat from '../../../lib/phone-format';
import Address from '../../../types/address';
import AddressValidation from '../../address-validation';
import useCustomer from "../../../hooks/use-customer";

interface Props {
  customer: Customer;
}

const AdminUserAddressForm: React.FC<Props> = ({ customer }) => {
  const admin = useCustomer();

  // address
  const [addressCompany, setAddressCompany] = useState(
    customer.address.company
  );
  const [address1, setAddress1] = useState(customer.address.address1);
  const [address2, setAddress2] = useState(customer.address.address2);
  const [addressCity, setAddressCity] = useState(customer.address.city);
  const [addressPostalCode, setAddressPostalCode] = useState(
    customer.address.postalCode
  );
  const [addressCountry, setAddressCountry] = useState(
    customer.address.country
  );
  const [addressState, setAddressState] = useState(customer.address.state);
  const [addressPhone, setAddressPhone] = useState(customer.address.phone);

  // current stored address should have already been validated
  const [validAddress, setValidAddress] = useState(true);

  const [addressMessage, setAddressMessage] = useState('');

  const [editCustomer] = useEditCustomer();
  const states = useStates();
  const countries = useCountries();

  const mapCountryToOption = (country) => {
    return {
      label: country.name,
      value: country.code,
    };
  };

  const mapStateToOption = (state) => {
    return {
      label: state.name,
      value: state.stateCode,
    };
  };

  const renderStateField = () => {
    const stateOptions = states
      .filter((s) => s.countryCode === addressCountry)
      .map(mapStateToOption);

    return stateOptions.length ? (
      <DropdownField
        id="addressState"
        label="State"
        value={addressState}
        options={[{ label: '- Choose One -', value: '' }, ...stateOptions]}
        onChange={(e) => {
          setValidAddress(false);
          setAddressState(e.target.value);
        }}
      />
    ) : (
      <InputField
        id="addressState"
        label="State"
        type="text"
        defaultValue={addressState}
        onChange={(e) => {
          setValidAddress(false);
          setAddressState(e.target.value);
        }}
      />
    );
  };

  const countryOptions = countries
    .filter(
      (c) =>
        c.code === 'US' || c.code === 'CA' || c.code === 'PR' || c.code === 'SE'
    )
    .map(mapCountryToOption);

  const handleAddressClick = async (e) => {
    e.preventDefault();
    setAddressMessage('');

    if (
      !address1 ||
      !addressCity ||
      !addressPostalCode ||
      !addressCountry ||
      !addressState ||
      !addressPhone
    ) {
      setAddressMessage(
        'All fields are required, except for Company and Address 2.'
      );
      return;
    }

    if (addressCompany.length > 50) {
      setAddressMessage('Company name cannot exceed 50 characters.');
      return;
    }

    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    const phoneRegex2 = /^\d-\d{3}-\d{3}-\d{4}$/;

    // Ensure phone number matches the format
    if (!phoneRegex.test(addressPhone) && !phoneRegex2.test(addressPhone)) {
      setAddressMessage('Invalid phone number format.');
      return;
    }

    // send address details update request
    // do graphql mutation
    const input: CustomerInput = {
      id: customer.id,
      accountId: process.env.SITE!,
      username: customer.username,
      firstName: customer.firstName,
      lastName: customer.lastName,
      address: {
        attention: `${customer.firstName} ${customer.lastName}`,
        company: addressCompany,
        address1: address1,
        address2: address2,
        county: '',
        country: addressCountry,
        city: addressCity,
        state: addressState,
        postalCode: addressPostalCode,
        phone: addressPhone,
        email: customer.address.email,
      },
      attributes: customer.attributes,
    };

    const omit = (key: string, value: string) =>
      key === '__typename' ? undefined : value;
    const attributes = JSON.parse(JSON.stringify(customer.attributes), omit);

    input.attributes = attributes;

    const { data } = await editCustomer({ input });
    if (data.editCustomer3.error) {
      setAddressMessage(data.editCustomer3.error);
    } else {
      setAddressMessage('Successfully updated address details.');
    }
  };

  const byPassValidation = admin && admin?.customer?.permissions.some(p => p === "GDST");

  return (
    <form>
      <h2>Address</h2>
      <InputField
        id="addressCompany"
        label="Company"
        type="text"
        maxLength={50}
        defaultValue={addressCompany}
        onChange={(e) => setAddressCompany(e.target.value)}
      />
      <InputField
        id="address1"
        label="Address 1"
        type="text"
        defaultValue={address1}
        onChange={(e) => {
          setValidAddress(false);
          setAddress1(e.target.value);
        }}
      />
      <InputField
        id="address2"
        label="Address 2"
        type="text"
        defaultValue={address2}
        onChange={(e) => setAddress2(e.target.value)}
      />
      <DropdownField
        id="addressCountry"
        label="Country"
        value={addressCountry}
        options={[{ label: '- Choose One -', value: '' }, ...countryOptions]}
        onChange={(e) => {
          setValidAddress(false);
          setAddressCountry(e.target.value);
        }}
      />
      <InputField
        id="addressCity"
        label="City"
        type="text"
        defaultValue={addressCity}
        onChange={(e) => {
          setValidAddress(false);
          setAddressCity(e.target.value);
        }}
      />
      <InputField
        id="addressPostalCode"
        label="Postal code"
        type="text"
        defaultValue={addressPostalCode}
        onChange={(e) => {
          setValidAddress(false);
          setAddressPostalCode(e.target.value);
        }}
      />
      {renderStateField()}
      <InputField
        id="addressPhone"
        label="Phone"
        hint="Use the format 123-456-7890 or 1-123-456-7890 (with country code)."
        type="tel"
        autoComplete="tel"
        defaultValue={addressPhone}
        onChange={(e) => setAddressPhone(phoneFormat(e.target.value))}
      />
      {!byPassValidation &&
        <AddressValidation
          address={{
            attention: '',
            company: addressCompany,
            address1,
            address2,
            county: '',
            city: addressCity,
            state: addressState,
            postalCode: addressPostalCode,
            country: addressCountry,
            phone: addressPhone,
            email: customer.address.email,
          }}
          updateAddress={(sa: Address) => {
            setAddressCountry(sa.country);
            setAddressState(sa.state);
            setAddressCity(sa.city);
            setAddressPostalCode(sa.postalCode);
            setAddress1(sa.address1);
            setAddress2(sa.address2);

            setValidAddress(true);
          }}
        />
      }
      {(validAddress || byPassValidation) &&
        <Button text="Save" onClick={handleAddressClick}/>
      }
      {addressMessage && <p>{addressMessage}</p>}
    </form>
  );
};

export default AdminUserAddressForm;
